import { useEmployeeTestMode } from '@/hooks/useEmployeeTestMode';
import { FlaskConical, Radio } from 'lucide-react';
import { useState } from 'react';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { toast } from 'sonner';

/**
 * Persistent banner shown at top of admin layout.
 * Displays current environment mode with a toggle switch between
 * "Live Production" and "Employee Test Mode".
 */
export function EmployeeTestBanner() {
  const { enabled, loading, refetch } = useEmployeeTestMode();
  const [toggling, setToggling] = useState(false);

  if (loading) return null;

  const handleToggle = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      const newState = !enabled;
      
      // Update employee_only_mode in demo_settings
      const { data: row, error: fetchErr } = await supabase
        .from('demo_settings' as any)
        .select('id')
        .limit(1)
        .maybeSingle();

      if (fetchErr || !row) {
        console.error('Failed to fetch demo_settings row:', fetchErr);
        throw new Error(fetchErr?.message || 'No demo_settings row found (are you signed in as admin?)');
      }

      const { error: updateErr, data: updateData, count } = await supabase
        .from('demo_settings' as any)
        .update({ employee_only_mode: newState } as any)
        .eq('id', (row as any).id)
        .select();

      console.log('Toggle update result:', { updateErr, updateData, count, newState });

      if (updateErr) {
        console.error('Failed to update demo_settings:', updateErr);
        throw updateErr;
      }

      // Audit log entry — capture who changed the toggle and old/new values
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const { data: adminRow } = user
          ? await supabase
              .from('admin_users' as any)
              .select('email')
              .eq('user_id', user.id)
              .maybeSingle()
          : { data: null as any };

        await supabase.from('admin_audit_log' as any).insert({
          actor_id: user?.id ?? null,
          actor_email: (adminRow as any)?.email ?? user?.email ?? null,
          action: 'demo_settings.toggle_employee_only_mode',
          target_type: 'demo_settings',
          target_id: (row as any).id,
          before_state: { employee_only_mode: enabled },
          after_state: { employee_only_mode: newState },
          request_id: crypto.randomUUID(),
        } as any);
      } catch (auditErr) {
        console.error('Failed to write audit log for demo toggle:', auditErr);
      }

      // Wait briefly for DB to settle, then refetch
      await new Promise(r => setTimeout(r, 300));
      refetch();
      toast.success(newState ? 'Switched to Employee Test Mode' : 'Switched to Live Production');
    } catch (e: any) {
      console.error('Toggle error:', e);
      toast.error('Failed to toggle mode: ' + (e.message || 'Unknown error'));
    } finally {
      setToggling(false);
    }
  };

  return (
    <div
      className={`text-white text-center py-1.5 px-4 text-sm font-semibold flex items-center justify-center gap-3 z-50 transition-colors duration-300 ${
        enabled ? 'bg-blue-600' : 'bg-emerald-600'
      }`}
    >
      {enabled ? (
        <>
          <FlaskConical className="w-4 h-4" />
          EMPLOYEE TEST MODE — All applicants treated as internal testers
        </>
      ) : (
        <>
          <Radio className="w-4 h-4 animate-pulse" />
          LIVE PRODUCTION
        </>
      )}

      <button
        onClick={handleToggle}
        disabled={toggling}
        className={`ml-4 relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/40 ${
          enabled ? 'bg-blue-400' : 'bg-emerald-400'
        } ${toggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
        title={enabled ? 'Switch to Live Production' : 'Switch to Employee Test Mode'}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
            enabled ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </button>
      <span className="text-xs opacity-80">
        {enabled ? 'Test' : 'Live'}
      </span>
    </div>
  );
}
